import { Breakpoint, SxProps, Theme } from '@mui/material';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { Box } from './Box';
import { Button, ButtonProps } from './Button';
import { Divider } from './Divider';
import { SafeArea } from './SafeArea';
import { TitleAndClose } from './TitleAndClose';

export interface ButtonPropsWithLabel extends ButtonProps {
  label?: string | React.ReactNode;
  target?: string;
}

export interface DialogProps {
  open: boolean;
  title?: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  titleAlignment?: 'left' | 'center';
  sx?: SxProps<Theme>;
  onClose?: () => void;
  fullScreen?: boolean;
  fullWidth?: boolean;
  titleVariant?: 'h5' | 'h6';
  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  actions?: React.ReactNode | ButtonPropsWithLabel[];
  actionsJustify?: 'flex-start' | 'center' | 'flex-end' | 'space-between';
  dividers?: boolean;
  noWrap?: boolean;
  spacing?: number;
  children: React.ReactNode;
  maxWidth?: false | Breakpoint;
}

export const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  subtitle,
  titleAlignment = 'left',
  sx,
  titleVariant,
  fullScreen,
  leftAdornment,
  rightAdornment,
  actions,
  actionsJustify,
  dividers = false,
  fullWidth = true,
  noWrap = false,
  spacing = 2,
  children,
  maxWidth,
}) => {
  const mainWhitespace = spacing;
  const showTitleSpace = title || onClose || rightAdornment || leftAdornment;

  const styles = {
    ...(!fullScreen && {
      '& .MuiDialog-paper': { mx: 2, width: 'calc(100% - 32px)' },
    }),
    ...sx,
  };
  return (
    <MuiDialog
      disableRestoreFocus
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      sx={styles}
      scroll="paper"
    >
      {showTitleSpace ? (
        <SafeArea top={fullScreen} mode="padding">
          <TitleAndClose
            title={title}
            subtitle={subtitle}
            titleAlignment={titleAlignment}
            onClose={onClose}
            noWrap={noWrap}
            leftAdornment={leftAdornment}
            rightAdornment={rightAdornment}
            titleVariant={titleVariant}
          ></TitleAndClose>
        </SafeArea>
      ) : (
        <Box mt={mainWhitespace}></Box>
      )}
      {dividers && <Divider />}
      <DialogContent
        sx={{
          p: mainWhitespace,
          pt: dividers ? mainWhitespace : 0,
          position: 'relative',
        }}
      >
        {children}
      </DialogContent>

      {dividers && !!actions && <Divider />}
      {!!actions && (
        <SafeArea bottom={fullScreen} mode="padding">
          <DialogActions sx={{ p: mainWhitespace, justifyContent: actionsJustify }}>
            {Array.isArray(actions) &&
              actions.map((buttonProps, i) => (
                <Button key={i} {...buttonProps}>
                  {buttonProps.children || buttonProps.label}
                </Button>
              ))}
            {!Array.isArray(actions) && actions}
          </DialogActions>
        </SafeArea>
      )}
    </MuiDialog>
  );
};
